/**
 * Dialog for saving template remotely with name validation
 */

import React, { useState, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import PropTypes from "prop-types";
import ModalBase from "../../components/Modals/ModalBase";
import { templateNameExists } from "../storage/templateStorage";
import "./DesktopManager.css";

const SaveTemplateDialog = ({ visible, onHide, onSave, existingName = "" }) => {
  const [templateName, setTemplateName] = useState(existingName || "");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (visible) {
      setTemplateName(existingName || "");
      setError("");
    }
  }, [visible, existingName]);

  const validateName = (name) => {
    if (!name || name.trim() === "") {
      return "Nome do template é obrigatório";
    }
    if (name.trim().length < 3) {
      return "Nome deve ter pelo menos 3 caracteres";
    }
    if (name.trim().length > 50) {
      return "Nome deve ter no máximo 50 caracteres";
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = validateName(templateName);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError("");

    try {
      // Check for duplicate name (only if it's a new template or name changed)
      const trimmedName = templateName.trim();
      if (trimmedName.toLowerCase() !== existingName.toLowerCase()) {
        const existingTemplate = await templateNameExists(trimmedName);
        if (existingTemplate) {
          setError("Já existe um template com este nome. Escolha outro nome.");
          setSaving(false);
          return;
        }
      }

      await onSave(trimmedName);
      // Parent component should close the dialog on success
    } catch (err) {
      console.error("Error saving template:", err);
      setError(err.message || "Falha ao salvar template");
    } finally {
      setSaving(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !saving) {
      handleSave();
    }
  };

  const footer = (
    <div className="modal-footer">
      <Button
        label="Cancelar"
        icon="pi pi-times"
        onClick={onHide}
        className="p-button-text"
        disabled={saving}
        data-evo-id="savetemplatedialog-btn-cancel"
      />
      <Button
        label="Salvar"
        icon="pi pi-check"
        onClick={handleSave}
        disabled={saving}
        loading={saving}
        data-evo-id="savetemplatedialog-btn-save"
      />
    </div>
  );

  return (
    <ModalBase
      header="Salvar Template"
      visible={visible}
      onHide={onHide}
      footer={footer}
      size="small"
      variant="form"
      closable={!saving}
      className="save-workspace-dialog"
    >
      <div className="p-fluid">
        <div className="p-field">
          <label htmlFor="template-name">Nome do Template</label>
          <InputText
            id="template-name"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Digite o nome do template"
            autoFocus
            disabled={saving}
            data-evo-id="savetemplatedialog-input-name"
          />
          {error && (
            <Message
              severity="error"
              text={error}
              className="save-workspace-error"
            />
          )}
          <small
            id="template-name-help"
            className="p-text-secondary save-workspace-help"
          >
            Template salva apenas indicadores e tipo de gráfico do chart
            selecionado
          </small>
        </div>
      </div>
    </ModalBase>
  );
};

SaveTemplateDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  existingName: PropTypes.string,
};

export default SaveTemplateDialog;
